import { Checkbox, Typography } from "@material-ui/core";
import { DictionaryItem } from "constants/models";
import { computed } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { FieldInputProps } from "react-final-form";
import { ValueType } from "react-select";
import InfiniteScroll from "react-infinite-scroll-component";

import TrimmedText from "components/TrimmedText";
import Placeholder from "components/Placeholder";
import PublishStatus from "components/PublishStatus";

import {
  Container,
  Wrapper,
  Image,
  Info,
  Text,
  ScrollLoader,
  PublishStatusContainer,
  PublishStatusWrapper,
  ControlWrapper,
} from "./styles";

type Option = {
  value: {
    title: string;
    imageUrl: string;
    id: string;
    duration: string;
    publishStatus?: string;
  };
  label: string;
  isDisabled?: boolean;
};

type Props = {
  input: FieldInputProps<any>;
  onChange: (item: ValueType<DictionaryItem>) => void;
};

export type CardListExternalProps = {
  options: Option[];
  multi?: boolean;
  required?: boolean;
  withInfinityScroll?: boolean;
  hasMore?: boolean;
  height?: string;
  searchValue?: string;
  noItemsPlaceholderText?: string;
  scrollableTarget?: string;
  fetchMore?: () => void;
};

@observer
class CardList extends React.Component<Props & CardListExternalProps> {
  get input() {
    const { input } = this.props;
    return input;
  }

  @computed
  get currentOptions() {
    const { input } = this.props;
    const value = input?.value || [];

    return value.map((item: any) => ({ label: item.title, value: item }));
  }

  onChange = (option: Option) => () => {
    const { onChange } = this.props;

    if (this.input?.value.id === option.value.id) {
      onChange(undefined);
    } else {
      onChange(option);
    }
  };

  onChangeMulti = (option: Option) => () => {
    const { onChange, required } = this.props;

    let items = this.currentOptions || [];
    if (this.isOptionCheckedMulti(option)) {
      items = items.filter(
        (item: DictionaryItem) => item.value.id !== option.value.id,
      );
    } else {
      items.push(option);
    }

    if (!required || items.length > 0) {
      onChange(items);
    }
  };

  isOptionChecked = (option: Option): boolean => {
    return this.input?.value.id === option.value.id;
  };

  isOptionCheckedMulti = (option: Option): boolean => {
    return !!this.currentOptions.find(
      (currentOption: any) => currentOption.value.id === option.value.id,
    );
  };

  renderItems = () => {
    const { options, multi } = this.props;

    return options.map(option => {
      const checked = !multi
        ? this.isOptionChecked(option)
        : this.isOptionCheckedMulti(option);

      return (
        <Wrapper
          key={option.value.id}
          checked={checked}
          isDisabled={option.isDisabled}
        >
          <Info>
            <Image image={option.value.imageUrl} />
            <Text>
              <TrimmedText lines={1} text={option.value.title}>
                <Typography variant="h4">{option.value.title}</Typography>
              </TrimmedText>
              <Typography variant="subtitle1">
                {option.value.duration}
              </Typography>
            </Text>
          </Info>
          <ControlWrapper>
            {option.value?.publishStatus && (
              <PublishStatusContainer>
                <PublishStatusWrapper>
                  <PublishStatus status={option.value?.publishStatus} />
                </PublishStatusWrapper>
              </PublishStatusContainer>
            )}
            <Checkbox
              color="primary"
              onClick={
                !multi ? this.onChange(option) : this.onChangeMulti(option)
              }
              checked={checked}
              disabled={option.isDisabled}
            />
          </ControlWrapper>
        </Wrapper>
      );
    });
  };

  render() {
    const {
      options,
      height,
      hasMore,
      searchValue,
      noItemsPlaceholderText,
      withInfinityScroll,
      scrollableTarget,
      fetchMore,
    } = this.props;

    return (
      <Container>
        {options.length ? (
          withInfinityScroll ? (
            <InfiniteScroll
              dataLength={options.length}
              hasMore={!!hasMore}
              next={fetchMore}
              height={height ? `calc(100vh - ${height})` : undefined}
              loader={<ScrollLoader />}
              scrollableTarget={scrollableTarget}
            >
              {this.renderItems()}
            </InfiniteScroll>
          ) : (
            this.renderItems()
          )
        ) : (
          <Placeholder
            height={132}
            width={132}
            text={
              searchValue
                ? "No matches found. Please try another search."
                : noItemsPlaceholderText
            }
          />
        )}
      </Container>
    );
  }
}

export default CardList;
